import { useEffect, useRef } from 'react'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

/** In welcher Größe der Canvas aufgehen soll. */
export type CanvasShortcutTarget = 'maximized' | 'split'

interface UseCanvasPanelShortcutOptions {
  /**
   * Nur im sichtbaren Tab (TabStack-Falle) — aber UNABHÄNGIG davon, ob der
   * Canvas offen ist. Anders als beim Werkzeug-Kürzel: dieses hier soll ihn ja
   * gerade hereinholen.
   */
  enabled: boolean
  /** Bekommt die Zielgröße; ob geöffnet oder geschlossen wird, entscheidet der Aufrufer. */
  onToggle: (target: CanvasShortcutTarget) => void
}

/**
 * `C` und `⇧C` holen den Projekt-Canvas herein und schicken ihn wieder weg.
 * Jede Taste gehört einer Größe: `C` der **maximierten**, `⇧C` dem **Split**
 * (die Regel selbst steht in `nextCanvasPanelState`).
 *
 * Die Taste steht für Canvas. `N` gehört dem neuen **Notizen**
 * (`useCanvasPlacementShortcut`) — dieselbe Aufteilung wie in FigJam, wo die
 * Fläche und das Element darauf getrennte Tasten haben.
 *
 * Blanker Buchstabe (⇧ zählt hier nicht als Modifikator), also mit denselben
 * Vorsichten wie die Projekt-Kürzel (A/B/F in `useProjectKeyboardShortcuts`)
 * und die Werkzeuge (V/H): nichts während getippt wird, nichts bei offenem
 * Menü. ⌘/⌃/⌥ bleiben ausgeschlossen — auf ⌘C liegt das Kopieren.
 *
 * @example
 * useCanvasPanelShortcut({ enabled: isActiveTab, onToggle: handleToggleCanvas })
 */
export function useCanvasPanelShortcut({ enabled, onToggle }: UseCanvasPanelShortcutOptions) {
  const onToggleRef = useRef(onToggle)
  onToggleRef.current = onToggle

  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘C kopiert — ohne diese Zeile käme der Canvas dabei jedes Mal mit
      // hoch. ⇧ allein ist dagegen frei und wählt hier die Größe.
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Ein offenes Menü behält seine Tasten für sich — die Konvention dieser
      // App, siehe `useProjectKeyboardShortcuts` und `useProjectNavigation`.
      if (document.querySelector('[role="menu"]')) return
      // Im Text einer Notiz und in jedem Aufgabenfeld ist „c" ein
      // Buchstabe.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Die Tasten der **Aufgaben-Details** gehören ihnen (CONTEXT.md): Dort
      // steht der Fokus meist auf einem Knopf, an dem der INPUT/TEXTAREA-Wächter
      // oben vorbeigeht.
      if (isInsideTaskDetails(e.target)) return

      // `code` statt `key`, damit das Kürzel auf jedem Layout dort liegt, wo
      // es eingezeichnet ist.
      if (e.code !== 'KeyC') return

      e.preventDefault()
      onToggleRef.current(e.shiftKey ? 'split' : 'maximized')
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled])
}
